import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router';
import { MmuFeatureFlagsService } from '../../feature-flags';
import { MmuLogger } from '../../logging';


interface IsFeatureEnabledOptions {
  /**
   * Whether the feature should be expected to be enabled or disabled.
   */
  shouldBeEnabled?: boolean;

  /**
   * The URL to redirect to if navigation is not allowed. If not provided, no redirection will be performed and navigation will just be
   * denied.
   */
  redirectTo?: string;
}

/**
 * Feature flag route guard factory for Miele MOVE. Check if a given feature is enabled or disabled and allow or deny navigation.
 *
 * @param {string} featureName The name of the feature flag to check.
 * @param options Options for the guard.
 * @returns {CanActivateFn | CanActivateChildFn}
 *
 * @example ```typescript
 * {
 *   path: 'protected',
 *   component: ProtectedComponent,
 *   canActivate: [hasOrganization()]
 * }
 * ```
 */
export function isFeatureEnabled(
  featureName: string,
  options: Partial<IsFeatureEnabledOptions> = {}
): CanActivateFn | CanMatchFn | CanActivateChildFn {

  options = {
    shouldBeEnabled: true,
    ...options
  };

  return (): UrlTree | boolean => {
    const featureFlagsService = inject(MmuFeatureFlagsService);
    const shouldBeEnabled = options.shouldBeEnabled;
    const allowNavigation = featureFlagsService.isFeatureEnabled(featureName) === shouldBeEnabled;

    if (allowNavigation) {
      return true;
    }

    const router = inject(Router);
    const logger = inject(MmuLogger);
    logger.warn(`Access of page is denied because the feature flag "${featureName}" is ${shouldBeEnabled ? 'disabled' : 'enabled'}.`);
    return (options.redirectTo) ? router.parseUrl(options.redirectTo) : false;
  };
}
